import { Button, FileInput, Input, Modal } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import Common from "@utilities/common";
import { FC, useEffect, useRef, useState } from "react";
import { Cropper, CropperRef, ImageRestriction } from "react-advanced-cropper";
import { AiOutlineUpload } from "react-icons/ai";
import { VscLoading } from "react-icons/vsc";
import { toast } from "react-toastify";

interface Props {
    id: string;
    name: string;
    type: "image" | "video";
    fileSetings: {
        maximumSize: number;
        aspectRatio?: number;
        accept: string;
    };
    inputProps: {
        value: string;
        label: string;
        placeholder?: string;
        errors?: string;
        required?: boolean;
    };
    repository:
        | "articleBanner"
        | "articleThumbnail"
        | "brands"
        | "categories"
        | "gallery"
        | "platforms"
        | "products"
        | "tags";
    handleChange: (data: any) => void;
    callbackFunction?: (file: File) => void;
}

const UploadFile: FC<Props> = ({
    id,
    name,
    type,
    fileSetings,
    repository,
    inputProps = {
        errors: "",
        placeholder: "",
        value: "",
        label: "",
        required: true,
    },
    callbackFunction,
    handleChange,
}) => {
    const [image, setImage] = useState<File | null>();
    const [isShowCropper, setIsShowCropper] = useState(false);

    const cropperRef = useRef<CropperRef>(null);

    useEffect(() => {
        if (image) {
            setIsShowCropper(true);
        } else {
            setIsShowCropper(false);
        }
    }, [image]);

    const { mutate, isLoading } = useMutation(
        ["uploadFile"],
        (file: Blob) =>
            // @ts-ignore
            Common.api[repository].UploadFile({
                id: +id,
                file: new File([file], name + ".png", {
                    type: "image/png",
                }),
            }),
        {
            onSuccess: ({ data }: any) => {
                handleChange(data);
                handleCloseCropper();
            },
        },
    );

    const handleOpenCropper = (file: File | null) => {
        if (!file) {
            return null;
        }

        if ((file?.size || 0) > fileSetings.maximumSize) {
            toast.warn("حجم فایل ارسالی بیش از حد مجاز میباشد");

            return null;
        }

        if (type === "video") {
            mutate(file);

            return null;
        }

        setImage(file);
        setIsShowCropper(true);
    };

    const handleCloseCropper = () => {
        setImage(null);
        setIsShowCropper(false);
    };

    const handleUpload = () => {
        if (cropperRef.current) {
            const data = cropperRef.current.getCanvas();

            data?.toBlob((blob) => {
                if (repository === "gallery") {
                    callbackFunction &&
                        callbackFunction(
                            new File([blob || new Blob()], name + ".png", {
                                type: "image/png",
                            }),
                        );
                    handleCloseCropper();
                } else {
                    mutate(blob || new Blob());
                }
            });
        }
    };

    return (
        <div>
            <FileInput
                onChange={(file) => handleOpenCropper(file)}
                value={image || new File([], "")}
                accept={fileSetings.accept}
                classNames={{
                    input: "border-0 p-0",
                }}
                valueComponent={() => (
                    <Input.Wrapper
                        id={id}
                        label={inputProps.label}
                        required={inputProps.required}
                        error={inputProps.errors}
                        classNames={{
                            error: "text-sm",
                            label: "mb-2 text-gray-700 font-medium",
                        }}>
                        <Input
                            variant="filled"
                            color="blue"
                            id={id}
                            name={id}
                            type="text"
                            placeholder={inputProps.placeholder}
                            disabled={isLoading}
                            value={inputProps.value}
                            invalid={!!inputProps.errors}
                            icon={
                                isLoading ? (
                                    <VscLoading className="animate-spin text-2xl" />
                                ) : (
                                    <AiOutlineUpload />
                                )
                            }
                        />
                    </Input.Wrapper>
                )}
            />

            <Modal
                opened={isShowCropper}
                classNames={{
                    modal: "p-0",
                }}
                closeOnClickOutside={false}
                withCloseButton={false}
                onClose={handleCloseCropper}>
                <div className="relative">
                    <Cropper
                        ref={cropperRef}
                        src={image ? URL.createObjectURL(image) : ""}
                        className={"cropper"}
                        stencilProps={{
                            aspectRatio: fileSetings.aspectRatio,
                        }}
                        imageRestriction={ImageRestriction.stencil}
                    />

                    <div className="absolute w-full bottom-0 text-center py-2 px-1 z-10">
                        <Button
                            color="blue"
                            className="mx-1"
                            loading={isLoading}
                            disabled={isLoading}
                            onClick={handleUpload}>
                            ذخیره
                        </Button>

                        <Button
                            color="red"
                            className="mx-1"
                            disabled={isLoading}
                            onClick={handleCloseCropper}>
                            انصراف
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default UploadFile;
